import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
  useTheme
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import usePlanBoardStore from '@/store/hr/PlanBoardStore';
import { TaskStatusChip } from './TaskStatusChip';

interface EmployeeDayDialogProps {
  open: boolean;
  employeeId: string | null;
  date: string;
  scope?: 'org' | 'team';
  onClose: () => void;
}

const timeOf = (value: string | null) =>
  value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

/** A stat pill, matching the TeamAttendance idiom. */
const Pill: React.FC<{ label: string; value: React.ReactNode; tone?: string }> = ({
  label,
  value,
  tone
}) => {
  const theme = useTheme();
  const color = tone || theme.palette.primary.main;
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 110,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        bgcolor: `${color}15`,
        border: `1px solid ${color}30`
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.75 }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 700 }}>{value}</Typography>
    </Box>
  );
};

/**
 * One employee's day: what they planned, what landed, and why the rest didn't.
 */
const EmployeeDayDialog: React.FC<EmployeeDayDialogProps> = ({
  open,
  employeeId,
  date,
  scope,
  onClose
}) => {
  const theme = useTheme();
  const dayDetail = usePlanBoardStore((s) => s.dayDetail);
  const detailLoading = usePlanBoardStore((s) => s.detailLoading);
  const fetchEmployeeDay = usePlanBoardStore((s) => s.fetchEmployeeDay);
  const clearDayDetail = usePlanBoardStore((s) => s.clearDayDetail);

  useEffect(() => {
    if (open && employeeId) fetchEmployeeDay(employeeId, date, scope);
    if (!open) clearDayDetail();
  }, [open, employeeId, date, scope, fetchEmployeeDay, clearDayDetail]);

  const att = dayDetail?.attendance;
  const tasks = dayDetail?.tasks || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}`, pb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {dayDetail?.employee.name || 'Loading…'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {new Date(`${date}T12:00:00`).toLocaleDateString(undefined, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}
          {dayDetail?.employee.dept_name ? ` · ${dayDetail.employee.dept_name}` : ''}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {detailLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
              <Pill label="Punched in" value={timeOf(att?.punch_in || null)} tone={theme.palette.success.main} />
              <Pill label="Punched out" value={timeOf(att?.punch_out || null)} tone={theme.palette.error.main} />
              <Pill
                label="Hours"
                value={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {att?.total_hours ?? '—'}
                    {att?.is_retro && (
                      // total_hours on a retro EOD is a standard workday the system
                      // filled in, not time anyone actually measured. Say so.
                      <Tooltip title="Filled in by a retroactive end-of-day report — this is a standard workday, not measured time.">
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      </Tooltip>
                    )}
                  </Box>
                }
                tone={theme.palette.primary.main}
              />
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Tasks ({tasks.length})
            </Typography>

            {tasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No plan was submitted for this day.
              </Typography>
            ) : (
              tasks.map((task) => (
                <Box
                  key={task.id}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 2
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600 }}>{task.title}</Typography>
                      {task.description && (
                        <Typography variant="body2" color="text.secondary">
                          {task.description}
                        </Typography>
                      )}
                    </Box>
                    <TaskStatusChip status={task.status} />
                  </Box>

                  {task.carry_count > 0 && (
                    // Two DIFFERENT numbers: carries are events, age is calendar days.
                    // A task can be picked up weeks later, so these must not be conflated.
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={`Carried ${task.carry_count}× · ${task.age_days} days old`}
                      sx={{ mt: 1, fontWeight: 600 }}
                    />
                  )}

                  {task.incomplete_reason && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}
                    >
                      Not completed: “{task.incomplete_reason}”
                    </Typography>
                  )}
                  {task.drop_reason && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}
                    >
                      Dropped: “{task.drop_reason}”
                    </Typography>
                  )}
                </Box>
              ))
            )}

            {att?.remarks && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Daily note
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'background.default',
                    borderRadius: 2,
                    p: 2.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    lineHeight: 1.7,
                    '& p': { margin: '0 0 8px 0' },
                    '& ul, & ol': { paddingLeft: '20px', margin: '0 0 8px 0' },
                    '& strong': { fontWeight: 700 }
                  }}
                  dangerouslySetInnerHTML={{ __html: att.remarks }}
                />
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeDayDialog;
